type ViewerExportCaseMetadata = {
    caseName?: string;
    caseNumber?: string;
    incidentDate?: string;
    preparedBy?: string;
    jurisdiction?: string;
    disclaimers?: string[];
};

type ViewerExportAnnotation = {
    id: string;
    worldPosition: [number, number, number];
    worldNormal?: [number, number, number];
    type: 'pin' | 'evidence' | 'witness' | 'vehicle' | 'poi' | 'damage';
    label: string;
    description?: string;
    exhibitId?: string;
    layerId?: string;
    linkedEvidenceIds?: string[];
    author?: string;
    createdAt?: string;
};

type ViewerExportBookmark = {
    id: string;
    name: string;
    note?: string;
    cameraPose: {
        position: [number, number, number];
        target: [number, number, number];
        fov?: number;
    };
};

type ViewerExportTourStep = {
    type: 'bookmark' | 'customView';
    bookmarkId?: string;
    cameraPose?: {
        position: [number, number, number];
        target: [number, number, number];
        fov?: number;
    };
    transitionDuration?: number;
    pauseDuration?: number;
};

type ViewerExportTour = {
    id: string;
    name: string;
    steps: ViewerExportTourStep[];
};

type ViewerExportEvidenceItem = {
    id: string;
    name?: string;
    frame?: number;
    exhibitId?: string;
    capturedAt?: string;
    capturedBy?: string;
    device?: string;
    tags?: string[];
    description?: string;
    position?: [number, number, number];
    target?: [number, number, number];
    image?: {
        filename?: string;
    };
};

type ViewerExportIncludeOptions = {
    annotations: boolean;
    bookmarks: boolean;
    tours: boolean;
};

type ViewerExportData = {
    include: ViewerExportIncludeOptions;
    annotations: ViewerExportAnnotation[];
    bookmarks: ViewerExportBookmark[];
    tours: ViewerExportTour[];
    evidenceItems: ViewerExportEvidenceItem[];
    caseMetadata?: ViewerExportCaseMetadata;
};

type ViewerExportDataInput = {
    include: ViewerExportIncludeOptions;
    annotations?: ViewerExportAnnotation[];
    annotationVisibleLayerIds?: string[];
    bookmarks?: ViewerExportBookmark[];
    tours?: ViewerExportTour[];
    evidenceItems?: ViewerExportEvidenceItem[];
    caseMetadata?: ViewerExportCaseMetadata;
};

const cloneAnnotation = (annotation: ViewerExportAnnotation): ViewerExportAnnotation => ({
    ...annotation,
    worldPosition: annotation.worldPosition ? [...annotation.worldPosition] as [number, number, number] : annotation.worldPosition,
    worldNormal: annotation.worldNormal ? [...annotation.worldNormal] as [number, number, number] : undefined,
    linkedEvidenceIds: annotation.linkedEvidenceIds?.slice()
});

const cloneBookmark = (bookmark: ViewerExportBookmark): ViewerExportBookmark => ({
    ...bookmark,
    cameraPose: {
        ...bookmark.cameraPose,
        position: [...bookmark.cameraPose.position] as [number, number, number],
        target: [...bookmark.cameraPose.target] as [number, number, number]
    }
});

const cloneTourStep = (step: ViewerExportTourStep): ViewerExportTourStep => ({
    ...step,
    cameraPose: step.cameraPose ? {
        ...step.cameraPose,
        position: [...step.cameraPose.position] as [number, number, number],
        target: [...step.cameraPose.target] as [number, number, number]
    } : undefined
});

const cloneTour = (tour: ViewerExportTour): ViewerExportTour => ({
    ...tour,
    steps: tour.steps.map(cloneTourStep)
});

const cloneEvidenceItem = (item: ViewerExportEvidenceItem): ViewerExportEvidenceItem => ({
    ...item,
    tags: item.tags?.slice(),
    position: item.position ? [...item.position] as [number, number, number] : undefined,
    target: item.target ? [...item.target] as [number, number, number] : undefined,
    image: item.image ? { ...item.image } : undefined
});

const buildViewerExportData = (input: ViewerExportDataInput): ViewerExportData => {
    const visibleLayerIds = new Set(input.annotationVisibleLayerIds ?? []);
    const includeAnnotations = !!input.include.annotations;
    const includeBookmarks = !!input.include.bookmarks;
    const includeTours = !!input.include.tours;

    const annotations = includeAnnotations ?
        (input.annotations ?? [])
        .filter(annotation => !annotation.layerId || visibleLayerIds.size === 0 || visibleLayerIds.has(annotation.layerId))
        .map(cloneAnnotation) :
        [];

    const bookmarks = includeBookmarks ?
        (input.bookmarks ?? []).map(cloneBookmark) :
        [];

    const tours = includeTours ?
        (input.tours ?? []).map(cloneTour) :
        [];

    const evidenceItems = (input.evidenceItems ?? []).map(cloneEvidenceItem);

    return {
        include: {
            annotations: includeAnnotations,
            bookmarks: includeBookmarks,
            tours: includeTours
        },
        annotations,
        bookmarks,
        tours,
        evidenceItems,
        caseMetadata: input.caseMetadata ? {
            ...input.caseMetadata,
            disclaimers: input.caseMetadata.disclaimers?.slice()
        } : undefined
    };
};

const buildTourAnimTrack = (tour: ViewerExportTour, bookmarksById: Map<string, ViewerExportBookmark>) => {
    const positions: number[] = [];
    const targets: number[] = [];
    const times: number[] = [];

    let time = 0;
    const frameRate = 30;

    const pushPose = (position: [number, number, number], target: [number, number, number]) => {
        positions.push(position[0], position[1], position[2]);
        targets.push(target[0], target[1], target[2]);
        times.push(time * frameRate);
    };

    for (const step of tour.steps) {
        let pose: ViewerExportTourStep['cameraPose'] | undefined;
        if (step.type === 'bookmark' && step.bookmarkId) {
            pose = bookmarksById.get(step.bookmarkId)?.cameraPose;
        } else if (step.type === 'customView') {
            pose = step.cameraPose;
        }

        if (pose?.position && pose?.target) {
            pushPose(pose.position, pose.target);
        }

        time += Math.max(0, step.transitionDuration ?? 0);
        time += Math.max(0, step.pauseDuration ?? 0);
    }

    if (times.length < 2) {
        return null;
    }

    return {
        name: `tour:${tour.id}`,
        duration: Math.max(time, 0.01),
        frameRate,
        target: 'camera',
        loopMode: 'none',
        interpolation: 'spline',
        smoothness: 0,
        keyframes: {
            times,
            values: {
                position: positions,
                target: targets
            }
        }
    };
};

const createInjectedRuntimeScript = (dataScriptId: string) => {
    return `<script>
(() => {
    const dataElement = document.getElementById('${dataScriptId}');
    if (!dataElement) {
        return;
    }

    let exportData = null;
    try {
        exportData = JSON.parse(dataElement.textContent || '{}');
    } catch {
        exportData = null;
    }

    if (!exportData || typeof exportData !== 'object') {
        return;
    }

    const parseHash = () => {
        const raw = String(window.location.hash || '').replace(/^#/, '');
        if (!raw) return null;
        const [kind, id] = raw.split(':');
        if (!kind || !id) return null;
        return { kind, id: decodeURIComponent(id) };
    };

    const buildTourAnimTrack = (tour, bookmarks) => {
        if (!tour || !Array.isArray(tour.steps)) {
            return null;
        }

        const bookmarkMap = new Map((Array.isArray(bookmarks) ? bookmarks : []).map((bookmark) => [bookmark.id, bookmark]));
        const positions = [];
        const targets = [];
        const times = [];
        const frameRate = 30;
        let time = 0;

        const pushPose = (position, target) => {
            positions.push(position[0], position[1], position[2]);
            targets.push(target[0], target[1], target[2]);
            times.push(time * frameRate);
        };

        for (const step of tour.steps) {
            let pose = null;
            if (step?.type === 'bookmark' && step?.bookmarkId) {
                pose = bookmarkMap.get(step.bookmarkId)?.cameraPose ?? null;
            } else if (step?.type === 'customView') {
                pose = step?.cameraPose ?? null;
            }

            if (pose?.position && pose?.target) {
                pushPose(pose.position, pose.target);
            }

            time += Math.max(0, Number(step?.transitionDuration ?? 0));
            time += Math.max(0, Number(step?.pauseDuration ?? 0));
        }

        if (times.length < 2) {
            return null;
        }

        return {
            name: 'nsiteTourTrack',
            duration: Math.max(time, 0.01),
            frameRate,
            target: 'camera',
            loopMode: 'none',
            interpolation: 'spline',
            smoothness: 0,
            keyframes: {
                times,
                values: {
                    position: positions,
                    target: targets
                }
            }
        };
    };

    window.__NSITE_EXPORT_BOOTSTRAP__ = (settings) => {
        const next = settings && typeof settings === 'object' ? settings : {};
        const bookmarkAction = parseHash();

        if (!next.camera || typeof next.camera !== 'object') {
            next.camera = {};
        }
        if (!Array.isArray(next.animTracks)) {
            next.animTracks = [];
        }

        if (bookmarkAction?.kind === 'bookmark') {
            const bookmark = (exportData.bookmarks || []).find((item) => item.id === bookmarkAction.id);
            if (bookmark?.cameraPose?.position && bookmark?.cameraPose?.target) {
                next.camera.position = bookmark.cameraPose.position.slice();
                next.camera.target = bookmark.cameraPose.target.slice();
                if (typeof bookmark.cameraPose.fov === 'number') {
                    next.camera.fov = bookmark.cameraPose.fov;
                }
                next.camera.startAnim = 'none';
                next.camera.animTrack = null;
            }
        } else if (bookmarkAction?.kind === 'tour') {
            const tour = (exportData.tours || []).find((item) => item.id === bookmarkAction.id);
            const track = buildTourAnimTrack(tour, exportData.bookmarks || []);
            if (track) {
                next.animTracks = [track, ...next.animTracks.filter((item) => item?.name !== 'nsiteTourTrack')];
                next.camera.startAnim = 'animTrack';
                next.camera.animTrack = 'nsiteTourTrack';
            }
        }

        return next;
    };

    const makeButton = (label, hash) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.style.display = 'block';
        button.style.width = '100%';
        button.style.margin = '4px 0';
        button.style.padding = '6px 8px';
        button.style.border = '1px solid rgba(255,255,255,0.25)';
        button.style.borderRadius = '6px';
        button.style.background = 'rgba(0,0,0,0.35)';
        button.style.color = '#fff';
        button.style.cursor = 'pointer';
        button.addEventListener('click', () => {
            window.location.hash = hash;
            window.location.reload();
        });
        return button;
    };

    document.addEventListener('DOMContentLoaded', () => {
        const panel = document.createElement('aside');
        panel.id = 'nsite-export-panel';
        panel.style.position = 'fixed';
        panel.style.top = '12px';
        panel.style.right = '12px';
        panel.style.width = '280px';
        panel.style.maxHeight = '80vh';
        panel.style.overflow = 'auto';
        panel.style.padding = '10px';
        panel.style.borderRadius = '8px';
        panel.style.background = 'rgba(0,0,0,0.55)';
        panel.style.backdropFilter = 'blur(4px)';
        panel.style.color = '#fff';
        panel.style.fontFamily = 'sans-serif';
        panel.style.fontSize = '12px';
        panel.style.zIndex = '9999';

        const heading = document.createElement('div');
        heading.textContent = 'NSite Export Data';
        heading.style.fontWeight = '700';
        heading.style.marginBottom = '8px';
        panel.appendChild(heading);

        if (Array.isArray(exportData.annotations) && exportData.annotations.length > 0) {
            const section = document.createElement('div');
            section.style.marginBottom = '8px';
            section.innerHTML = '<div style="font-weight:600;margin-bottom:4px;">Annotation pins</div>';
            const list = document.createElement('div');
            for (const annotation of exportData.annotations) {
                const row = document.createElement('div');
                row.textContent = '- ' + String(annotation.label || annotation.id);
                row.style.opacity = '0.9';
                row.style.margin = '2px 0';
                list.appendChild(row);
            }
            section.appendChild(list);
            panel.appendChild(section);
        }

        if (Array.isArray(exportData.bookmarks) && exportData.bookmarks.length > 0) {
            const section = document.createElement('div');
            section.style.marginBottom = '8px';
            section.innerHTML = '<div style="font-weight:600;margin-bottom:4px;">Bookmarks</div>';
            for (const bookmark of exportData.bookmarks) {
                section.appendChild(makeButton(bookmark.name || bookmark.id, 'bookmark:' + encodeURIComponent(bookmark.id)));
            }
            panel.appendChild(section);
        }

        if (Array.isArray(exportData.tours) && exportData.tours.length > 0) {
            const section = document.createElement('div');
            section.style.marginBottom = '8px';
            section.innerHTML = '<div style="font-weight:600;margin-bottom:4px;">Tours</div>';
            for (const tour of exportData.tours) {
                section.appendChild(makeButton('Play: ' + String(tour.name || tour.id), 'tour:' + encodeURIComponent(tour.id)));
            }
            panel.appendChild(section);
        }

        if (exportData.caseMetadata && typeof exportData.caseMetadata === 'object') {
            const section = document.createElement('div');
            section.style.opacity = '0.9';
            section.style.marginTop = '8px';
            section.innerHTML = '<div style="font-weight:600;margin-bottom:4px;">Case</div>';
            const metadata = exportData.caseMetadata;
            const lines = [metadata.caseName, metadata.caseNumber, metadata.incidentDate, metadata.preparedBy].filter(Boolean);
            for (const line of lines) {
                const row = document.createElement('div');
                row.textContent = String(line);
                row.style.margin = '2px 0';
                section.appendChild(row);
            }
            panel.appendChild(section);
        }

        document.body.appendChild(panel);
    });
})();
</script>`;
};

const injectViewerExportExtension = (html: string, data: ViewerExportData): string => {
    const dataScriptId = 'nsite-export-data';
    const serializedData = JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/<\//g, '<\\/');

    const dataScript = `<script id="${dataScriptId}" type="application/json">${serializedData}</script>`;
    const runtimeScript = createInjectedRuntimeScript(dataScriptId);

    let result = html;
    const settingsHook = 'const settingsJson = await settings;';
    const viewerHook = 'const viewer = await main(canvas, settingsJson, config);';

    if (!result.includes(settingsHook)) {
        throw new Error('Viewer export injection failed: missing settings bootstrap hook "const settingsJson = await settings;"');
    }

    if (!result.includes(viewerHook)) {
        throw new Error('Viewer export injection failed: missing viewer bootstrap hook "const viewer = await main(canvas, settingsJson, config);"');
    }

    result = result.replace(
        settingsHook,
        'const settingsJson = window.__NSITE_EXPORT_BOOTSTRAP__ ? window.__NSITE_EXPORT_BOOTSTRAP__(await settings) : await settings;'
    );
    result = result.replace(
        viewerHook,
        'const viewer = await main(canvas, settingsJson, config);\n                window.__NSITE_VIEWER__ = viewer;'
    );

    if (!result.includes('</body>')) {
        throw new Error('Viewer export injection failed: missing </body> insertion point for embedded export payload');
    }

    result = result.replace('</body>', `${dataScript}\n        ${runtimeScript}\n    </body>`);

    return result;
};

export {
    buildTourAnimTrack,
    buildViewerExportData,
    injectViewerExportExtension
};
export type {
    ViewerExportAnnotation,
    ViewerExportBookmark,
    ViewerExportCaseMetadata,
    ViewerExportData,
    ViewerExportDataInput,
    ViewerExportEvidenceItem,
    ViewerExportIncludeOptions,
    ViewerExportTour,
    ViewerExportTourStep
};
